/**
 * Simple in-memory cache utility for reducing API calls
 * Implements TTL-based caching with automatic cleanup
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

export class SimpleCache<T = any> {
  private cache = new Map<string, CacheEntry<T>>();
  private defaultTTL: number;

  constructor(defaultTTL: number = 5 * 60 * 1000) { // 5 minutes default
    this.defaultTTL = defaultTTL;
  }

  /**
   * Get cached data if it exists and hasn't expired
   */
  get(key: string): T | null {
    const entry = this.cache.get(key);
    
    if (!entry) {
      return null;
    }

    // Check if entry has expired
    if (Date.now() - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      return null;
    }

    return entry.data;
  }

  /**
   * Set cached data with optional TTL
   */
  set(key: string, data: T, ttl?: number): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl: ttl || this.defaultTTL
    });
  }

  /**
   * Check if key exists and is not expired
   */
  has(key: string): boolean {
    return this.get(key) !== null;
  }

  /**
   * Delete specific key
   */
  delete(key: string): boolean {
    return this.cache.delete(key);
  }

  /**
   * Clear all cached data
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * Clear expired entries
   */
  cleanup(): void {
    const now = Date.now();
    const entries = Array.from(this.cache.entries());
    for (const [key, entry] of entries) {
      if (now - entry.timestamp > entry.ttl) {
        this.cache.delete(key);
      }
    }
  }

  /**
   * Get cache statistics
   */
  getStats(): { size: number; keys: string[] } {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys())
    };
  }

  /**
   * Clear cache entries matching a pattern
   */
  clearPattern(pattern: string): number {
    let cleared = 0;
    const keys = Array.from(this.cache.keys());
    for (const key of keys) {
      if (key.includes(pattern)) {
        this.cache.delete(key);
        cleared++;
      }
    }
    return cleared;
  }
}

// Global cache instances for different data types
export const organizationCache = new SimpleCache(10 * 60 * 1000); // 10 minutes
export const matchCache = new SimpleCache(2 * 60 * 1000); // 2 minutes
export const userCache = new SimpleCache(5 * 60 * 1000); // 5 minutes
export const eventsCache = new SimpleCache<any[]>(30 * 1000); // 30 seconds - events can change frequently during matches

// Utility function to create cache keys
export const createCacheKey = (...parts: (string | number | undefined)[]): string => {
  return parts.filter(Boolean).join('_');
};

// Auto-cleanup every 5 minutes
setInterval(() => {
  organizationCache.cleanup();
  matchCache.cleanup();
  userCache.cleanup();
  eventsCache.cleanup();
}, 5 * 60 * 1000);
